import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { GameComponentProps } from '../core/types';
import { HintButton } from '../../shared/grid/HintButton';
import { UndoButton } from '../../shared/grid/UndoButton';
import { isGridArrowKey, moveGridSelection } from '../../shared/grid/navigation';
import { completedLineClues, isLineOverfilled } from './logic';
import { isNonogramPuzzle, type NonogramCell, type NonogramPuzzle } from './types';

type NonogramMove = { index: number; before: NonogramCell; after: NonogramCell };
type NonogramSavedState = {
  version: 1;
  board: NonogramCell[];
  selectedIndex: number;
  history: NonogramMove[];
  hintUsed: boolean;
  revealedWrong: number[];
};

function isMove(value: unknown, cellCount: number): value is NonogramMove {
  if (!value || typeof value !== 'object') return false;
  const move = value as Partial<NonogramMove>;
  return (
    Number.isInteger(move.index) &&
    move.index! >= 0 &&
    move.index! < cellCount &&
    (move.before === 0 || move.before === 1 || move.before === 2) &&
    (move.after === 0 || move.after === 1 || move.after === 2)
  );
}

function readSavedState(value: unknown, puzzle: NonogramPuzzle): NonogramSavedState {
  const cellCount = puzzle.width * puzzle.height;
  const fallback: NonogramSavedState = {
    version: 1,
    board: Array.from({ length: cellCount }, () => 0),
    selectedIndex: 0,
    history: [],
    hintUsed: false,
    revealedWrong: [],
  };
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<NonogramSavedState>;
  const board =
    Array.isArray(candidate.board) && candidate.board.length === cellCount
      ? candidate.board.map((cell) => (cell === 1 || cell === 2 ? cell : 0))
      : fallback.board;
  const selectedIndex =
    Number.isInteger(candidate.selectedIndex) &&
    candidate.selectedIndex! >= 0 &&
    candidate.selectedIndex! < cellCount
      ? candidate.selectedIndex!
      : 0;
  return {
    version: 1,
    board,
    selectedIndex,
    history: Array.isArray(candidate.history)
      ? candidate.history.filter((move) => isMove(move, cellCount))
      : [],
    hintUsed: candidate.hintUsed === true,
    revealedWrong: Array.isArray(candidate.revealedWrong)
      ? candidate.revealedWrong.filter(
          (index): index is number => Number.isInteger(index) && index >= 0 && index < cellCount,
        )
      : [],
  };
}

function lineMarks(
  board: readonly NonogramCell[],
  width: number,
  height: number,
  axis: 'row' | 'column',
  line: number,
): NonogramCell[] {
  return axis === 'row'
    ? board.slice(line * width, (line + 1) * width)
    : Array.from({ length: height }, (_, row) => board[row * width + line]);
}

export function NonogramGame({
  puzzle,
  savedState,
  disabled,
  onStateChange,
  onCharge,
  onComplete,
}: GameComponentProps) {
  if (!isNonogramPuzzle(puzzle.payload)) {
    throw new Error('Nonogram received an invalid puzzle payload.');
  }
  const nonogram = puzzle.payload;
  const initial = useMemo(() => readSavedState(savedState, nonogram), [savedState, nonogram]);
  const [board, setBoard] = useState(initial.board);
  const [selectedIndex, setSelectedIndex] = useState(initial.selectedIndex);
  const [history, setHistory] = useState(initial.history);
  const [hintUsed, setHintUsed] = useState(initial.hintUsed);
  const [revealedWrong, setRevealedWrong] = useState(new Set(initial.revealedWrong));
  const [inputMode, setInputMode] = useState<1 | 2>(1);
  const [status, setStatus] = useState('');
  const boardRef = useRef(board);
  const historyRef = useRef(history);
  const selectedRef = useRef(selectedIndex);
  const hintRef = useRef(hintUsed);
  const wrongRef = useRef(revealedWrong);
  const completionSent = useRef(false);
  const drag = useRef<{
    start: number;
    moved: boolean;
    value: NonogramCell;
    rightButton: boolean;
    touched: Set<number>;
  } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const persist = useCallback(
    (nextBoard = boardRef.current) => {
      onStateChange({
        version: 1,
        board: nextBoard,
        selectedIndex: selectedRef.current,
        history: historyRef.current,
        hintUsed: hintRef.current,
        revealedWrong: [...wrongRef.current],
      });
    },
    [onStateChange],
  );

  const select = useCallback(
    (index: number, focus = false) => {
      selectedRef.current = index;
      setSelectedIndex(index);
      persist();
      if (focus) {
        window.requestAnimationFrame(() => {
          gridRef.current?.querySelector<HTMLElement>(`[data-grid-index="${index}"]`)?.focus();
        });
      }
    },
    [persist],
  );

  const setCell = useCallback(
    (index: number, value: NonogramCell) => {
      if (disabled || boardRef.current[index] === value) return;
      const nextBoard = [...boardRef.current];
      const nextHistory = [
        ...historyRef.current,
        { index, before: nextBoard[index], after: value } satisfies NonogramMove,
      ];
      nextBoard[index] = value;
      const nextWrong = new Set(wrongRef.current);
      nextWrong.delete(index);
      boardRef.current = nextBoard;
      historyRef.current = nextHistory;
      selectedRef.current = index;
      wrongRef.current = nextWrong;
      setBoard(nextBoard);
      setHistory(nextHistory);
      setSelectedIndex(index);
      setRevealedWrong(nextWrong);
      persist(nextBoard);
    },
    [disabled, persist],
  );

  const cycle = useCallback(
    (index: number) => {
      const value = boardRef.current[index];
      setCell(index, value === 0 ? 1 : value === 1 ? 2 : 0);
    },
    [setCell],
  );

  const toggleCell = useCallback(
    (index: number, value: 1 | 2) => {
      setCell(index, boardRef.current[index] === value ? 0 : value);
    },
    [setCell],
  );

  const extendDrag = useCallback(
    (index: number) => {
      const gesture = drag.current;
      if (!gesture || gesture.touched.has(index)) return;
      gesture.moved = true;
      if (gesture.touched.size === 1) setCell(gesture.start, gesture.value);
      gesture.touched.add(index);
      setCell(index, gesture.value);
    },
    [setCell],
  );

  const startPointer = (event: ReactPointerEvent<HTMLButtonElement>, index: number) => {
    if (disabled || (event.button !== 0 && event.button !== 2)) return;
    if (event.button === 0 && event.pointerType !== 'touch') event.preventDefault();
    select(index);
    drag.current = {
      start: index,
      moved: false,
      value: event.button === 2 ? 2 : window.innerWidth <= 650 ? inputMode : 1,
      rightButton: event.button === 2,
      touched: new Set([index]),
    };
  };

  const endPointer = (index: number) => {
    const gesture = drag.current;
    if (!gesture) return;
    if (!gesture.moved && gesture.start === index) {
      if (gesture.rightButton) setCell(index, 2);
      else if (window.innerWidth <= 650) toggleCell(index, inputMode);
      else setCell(index, 1);
    }
    drag.current = null;
  };

  const onGridPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const pointerTarget =
      typeof document.elementFromPoint === 'function'
        ? document.elementFromPoint(event.clientX, event.clientY)
        : (event.target as HTMLElement);
    const target = pointerTarget?.closest<HTMLElement>('[data-grid-index]');
    const index = Number(target?.dataset.gridIndex);
    if (Number.isInteger(index)) extendDrag(index);
  };

  const undo = useCallback(() => {
    if (disabled || historyRef.current.length === 0) return;
    const move = historyRef.current.at(-1)!;
    const nextBoard = [...boardRef.current];
    nextBoard[move.index] = move.before;
    boardRef.current = nextBoard;
    historyRef.current = historyRef.current.slice(0, -1);
    selectedRef.current = move.index;
    const nextWrong = new Set(wrongRef.current);
    nextWrong.delete(move.index);
    wrongRef.current = nextWrong;
    setBoard(nextBoard);
    setHistory(historyRef.current);
    setSelectedIndex(move.index);
    setRevealedWrong(nextWrong);
    setStatus('Move undone');
    persist(nextBoard);
  }, [disabled, persist]);

  const revealIncorrect = useCallback(() => {
    if (disabled) return;
    const firstUse = !hintRef.current;
    const nextBoard = [...boardRef.current];
    const nextWrong = new Set<number>();
    nextBoard.forEach((value, index) => {
      if (value === 1 && !nonogram.solution[index]) {
        nextBoard[index] = 2;
        nextWrong.add(index);
      }
    });
    if (firstUse) onCharge('reveal-incorrect', 30_000, 'Reveal incorrect Nonogram fills');
    hintRef.current = true;
    wrongRef.current = nextWrong;
    boardRef.current = nextBoard;
    setHintUsed(true);
    setRevealedWrong(nextWrong);
    setBoard(nextBoard);
    setStatus(
      nextWrong.size === 0
        ? `No incorrect fills${firstUse ? ' · +30s' : ''}`
        : `${nextWrong.size} incorrect ${nextWrong.size === 1 ? 'fill' : 'fills'} changed to X${firstUse ? ' · +30s' : ''}`,
    );
    persist(nextBoard);
  }, [disabled, nonogram.solution, onCharge, persist]);

  useEffect(() => {
    const cancelDrag = () => {
      drag.current = null;
    };
    window.addEventListener('pointerup', cancelDrag);
    window.addEventListener('pointercancel', cancelDrag);
    return () => {
      window.removeEventListener('pointerup', cancelDrag);
      window.removeEventListener('pointercancel', cancelDrag);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isGridArrowKey(event.key)) {
        event.preventDefault();
        select(
          moveGridSelection(selectedRef.current, event.key, nonogram.width, nonogram.height),
          true,
        );
      } else if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        cycle(selectedRef.current);
      } else if (event.key.toLowerCase() === 'f') setCell(selectedRef.current, 1);
      else if (event.key.toLowerCase() === 'x') setCell(selectedRef.current, 2);
      else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        setCell(selectedRef.current, 0);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cycle, disabled, nonogram.height, nonogram.width, select, setCell]);

  useEffect(() => {
    if (
      completionSent.current ||
      !board.every((value, index) => (value === 1) === nonogram.solution[index])
    ) {
      return;
    }
    completionSent.current = true;
    onComplete({
      kind: 'solved',
      baseDeltaMs: hintUsed ? 0 : -10_000,
      message: 'Picture complete!',
      metadata: { hintUsed, spriteId: nonogram.spriteId, moveCount: history.length },
    });
  }, [board, hintUsed, history.length, nonogram.solution, nonogram.spriteId, onComplete]);

  const rowStates = useMemo(
    () =>
      nonogram.rowClues.map((clues, row) => {
        const marks = lineMarks(board, nonogram.width, nonogram.height, 'row', row);
        return {
          completed: completedLineClues(marks, clues),
          overfilled: isLineOverfilled(marks, clues),
        };
      }),
    [board, nonogram],
  );
  const columnStates = useMemo(
    () =>
      nonogram.columnClues.map((clues, column) => {
        const marks = lineMarks(board, nonogram.width, nonogram.height, 'column', column);
        return {
          completed: completedLineClues(marks, clues),
          overfilled: isLineOverfilled(marks, clues),
        };
      }),
    [board, nonogram],
  );
  const style = {
    '--nonogram-width': nonogram.width,
    '--nonogram-height': nonogram.height,
  } as CSSProperties;

  return (
    <section className="nonogram-game" aria-labelledby="nonogram-title" style={style}>
      <div className="nonogram-heading">
        <p className="eyebrow">Fill every run · mark known blanks with X</p>
      </div>
      <h1 id="nonogram-title" className="sr-only">
        Nonogram
      </h1>
      <div className="nonogram-layout">
        <div className="nonogram-clue-corner" aria-hidden="true" />
        <div className="nonogram-column-clues" aria-label="Column clues">
          {nonogram.columnClues.map((clues, column) => (
            <div
              key={column}
              className={`nonogram-column-clue ${columnStates[column].overfilled ? 'nonogram-clue--overfilled' : ''}`}
              aria-label={`Column ${column + 1}: ${clues.join(', ')}`}
            >
              {clues.map((clue, clueIndex) => (
                <span
                  className={
                    columnStates[column].completed[clueIndex] ? 'nonogram-clue--complete' : ''
                  }
                  key={clueIndex}
                >
                  {clue}
                </span>
              ))}
            </div>
          ))}
        </div>
        <div className="nonogram-row-clues" aria-label="Row clues">
          {nonogram.rowClues.map((clues, row) => (
            <div
              key={row}
              className={`nonogram-row-clue ${rowStates[row].overfilled ? 'nonogram-clue--overfilled' : ''}`}
              aria-label={`Row ${row + 1}: ${clues.join(', ')}`}
            >
              {clues.map((clue, clueIndex) => (
                <span
                  className={rowStates[row].completed[clueIndex] ? 'nonogram-clue--complete' : ''}
                  key={clueIndex}
                >
                  {clue}
                </span>
              ))}
            </div>
          ))}
        </div>
        <div
          ref={gridRef}
          role="grid"
          aria-label={`${nonogram.width} by ${nonogram.height} Nonogram grid`}
          className="nonogram-grid"
          onPointerMove={onGridPointerMove}
          onPointerLeave={() => {
            if (drag.current?.moved) drag.current = null;
          }}
        >
          {board.map((value, index) => {
            const row = Math.floor(index / nonogram.width);
            const column = index % nonogram.width;
            const label = value === 1 ? 'filled' : value === 2 ? 'X' : 'empty';
            return (
              <button
                type="button"
                role="gridcell"
                key={index}
                data-grid-index={index}
                tabIndex={selectedIndex === index ? 0 : -1}
                aria-selected={selectedIndex === index}
                aria-label={`Row ${row + 1}, column ${column + 1}, ${label}`}
                className={`nonogram-cell nonogram-cell--${label.toLowerCase()} ${selectedIndex === index ? 'nonogram-cell--selected' : ''} ${revealedWrong.has(index) ? 'nonogram-cell--revealed' : ''} ${(column + 1) % 4 === 0 && column < nonogram.width - 1 ? 'nonogram-cell--guide-right' : ''} ${(row + 1) % 4 === 0 && row < nonogram.height - 1 ? 'nonogram-cell--guide-bottom' : ''}`}
                disabled={disabled}
                onFocus={() => select(index)}
                onPointerDown={(event) => startPointer(event, index)}
                onPointerEnter={() => extendDrag(index)}
                onPointerUp={() => endPointer(index)}
                onPointerCancel={() => {
                  drag.current = null;
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (disabled) return;
                  select(index);
                  setCell(index, 2);
                }}
              >
                {value === 2 ? '×' : ''}
              </button>
            );
          })}
        </div>
      </div>
      <p className="logic-legend">
        Choose Fill or X note for taps and dragging. Tap the active mark again to clear it.
        Right-click still places an X.
      </p>
      <div className="nonogram-tools" aria-label="Cell tools">
        <button
          type="button"
          className={inputMode === 1 ? 'nonogram-tool--active' : ''}
          aria-pressed={inputMode === 1}
          disabled={disabled}
          onClick={() => setInputMode(1)}
        >
          ■ <small>Fill</small>
        </button>
        <button
          type="button"
          className={inputMode === 2 ? 'nonogram-tool--active' : ''}
          aria-pressed={inputMode === 2}
          disabled={disabled}
          onClick={() => setInputMode(2)}
        >
          × <small>X note</small>
        </button>
        <button type="button" disabled={disabled} onClick={() => setCell(selectedIndex, 0)}>
          ⌫ <small>Erase</small>
        </button>
      </div>
      <div className="grid-actions">
        <UndoButton count={history.length} disabled={disabled} onUndo={undo} />
        <HintButton
          label="Reveal incorrect"
          costSeconds={30}
          paid={hintUsed}
          disabled={disabled}
          onClick={revealIncorrect}
        />
      </div>
      <p className="sudoku-status" role="status" aria-live="polite">
        {status}
      </p>
    </section>
  );
}
